import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle, Navigation } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { InquiryForm } from "@/components/site/InquiryForm";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { useLang, useL } from "@/context/LanguageContext";
import { useSeo } from "@/hooks/useSeo";
import { CONTACT, WHATSAPP_ENABLED, PHONE_ENABLED } from "@/data/copy";
import { IMG } from "@/data/images";

// The pin and the surrounding view are both derived from CONTACT.coords, so
// moving the office means editing one place in data/copy.js.
const { lat, lng } = CONTACT.coords;
const MAP_COORDS = `${lat},${lng}`;

// A window of roughly ±0.8 km around the pin — close enough that the square is
// recognisable without the marker floating in an empty city view.
const PAD_LAT = 0.004;
const PAD_LNG = 0.008;
const MAP_BBOX = encodeURIComponent(
  [lng - PAD_LNG, lat - PAD_LAT, lng + PAD_LNG, lat + PAD_LAT].join(",")
);

export default function Contact() {
  const { t, isRtl } = useLang();
  const L = useL();
  // Product detail pages link here with ?product=<name> so the form arrives
  // pre-filled with whatever the visitor was looking at.
  const [searchParams] = useSearchParams();
  const presetProduct = searchParams.get("product") || "";
  useSeo({
    title: isRtl ? "تواصل معنا | شركة الأبرد" : "Contact | Al-Abrad",
    description: t.contactPage.sub,
  });

  const details = [
    { icon: MapPin, label: t.contactPage.addressLabel, value: L(CONTACT.address), href: null },
    PHONE_ENABLED && {
      icon: Phone,
      label: t.supportCta.call,
      value: CONTACT.phone,
      href: `tel:${CONTACT.phoneHref}`,
      ltr: true,
    },
    { icon: Mail, label: t.supportCta.email, value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: Clock, label: t.contactPage.hoursLabel, value: L(CONTACT.hours), href: null },
  ].filter(Boolean);

  const waHref = `https://wa.me/${CONTACT.whatsapp}`;

  return (
    <>
      <PageHero title={t.contactPage.title} subtitle={t.contactPage.sub} image={IMG.coldChain} crumb={t.nav.contact} />

      <section className="bg-cream py-24 lg:py-32">
        <div className="container-x grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* details */}
          <div>
            <Reveal>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-forest sm:text-4xl">
                {t.contactPage.infoTitle}
              </h2>
            </Reveal>
            <RevealGroup className="mt-10 grid gap-5 sm:grid-cols-2">
              {details.map((d, i) => {
                const Icon = d.icon;
                const inner = (
                  <>
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-forest text-gold-light">
                      <Icon className="h-6 w-6" strokeWidth={1.7} />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone">{d.label}</p>
                      <p dir={d.ltr ? "ltr" : undefined} className="mt-1 font-medium text-forest">
                        {d.value}
                      </p>
                    </div>
                  </>
                );
                return (
                  <RevealItem key={i} data-testid={`contact-detail-${i}`}>
                    {d.href ? (
                      <a
                        href={d.href}
                        className="flex items-start gap-4 rounded-2xl border border-line bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-forest/30"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div className="flex items-start gap-4 rounded-2xl border border-line bg-white p-6">{inner}</div>
                    )}
                  </RevealItem>
                );
              })}
            </RevealGroup>

            {WHATSAPP_ENABLED && (
              <Reveal i={2}>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <MessageCircle className="h-5 w-5" />
                  WhatsApp — {CONTACT.phone}
                </a>
              </Reveal>
            )}
          </div>

          {/* inquiry form */}
          <div>
            <Reveal>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-forest sm:text-4xl">
                {t.form.title}
              </h2>
            </Reveal>
            <Reveal i={1}>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-stone">{t.form.sub}</p>
            </Reveal>
            <Reveal i={2}>
              <div className="mt-8">
                <InquiryForm defaultProduct={presetProduct} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* map */}
      <section className="bg-mint py-20 lg:py-24">
        <div className="container-x">
          <div>
            <Reveal>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-forest sm:text-4xl">
                {t.contactPage.mapTitle}
              </h2>
            </Reveal>
            <Reveal i={1}>
              <div
                className="mt-10 overflow-hidden rounded-2xl border border-line"
                data-testid="contact-map"
              >
                <iframe
                  title="Al-Abrad location"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${MAP_BBOX}&layer=mapnik&marker=${MAP_COORDS}`}
                  className="h-[420px] w-full grayscale-[0.2]"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal i={2}>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${MAP_COORDS}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions"
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-forest/30 px-6 py-3 text-sm font-semibold text-forest transition-colors hover:bg-forest hover:text-cream"
              >
                <Navigation aria-hidden="true" className="h-4 w-4 rtl-flip" />
                {t.contactPage.directions}
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
